import React, { useEffect } from 'react';
import nodeLibrary from './nodeLibrary.json';
import NodePanel from './NodePanel';
import { saveWorkflow } from './workflowData';
import { getDefaultConfig } from './workflowHelpers';
import { recordPublishedWorkflow } from './workflowStorage';
import { computeChangeset, applyChangeset } from './workflowChangeset';
import { ExperimentChart } from './WorkflowMetrics';
import { SaveWorkflowModal } from './WorkflowModals';
import WorkflowEditorHeader from './WorkflowEditorHeader';
import StepCard from './StepCard';
import ConfigPanel from './ConfigPanel';
import { StepConnector } from './Connectors';
import { writeFile, deleteFile } from '../../../../utils/api';
import { API_BASE_URL } from '../../../../config/constants';
import { useWorkflowEditor } from './useWorkflowEditor';

const { nodes } = nodeLibrary;

function WorkflowEditor({ workflow, onBack, onStepHover, onSave, onSkillAdded, showOnboarding = false, canShowWriteButton = true, activeLoops = {}, showAgentMenu = false, onToggleAgentMenu, showEditorToggle = true, onToggleEditor, hideHeader = false, hideContentBelowHeader = false, onWriteToRepo, onToggleFileSystem, fileSystemOpen = true, onOpenSignInModal, writeToRepoHandlerRef }) {
  // Log mount/unmount
  useEffect(() => {
    console.log('[WorkflowEditor] MOUNTED with workflow:', {
      workflowId: workflow?.id,
      stepsCount: workflow?.steps?.length
    });
    return () => console.log('[WorkflowEditor] UNMOUNTED');
  }, []);

  // Log workflow prop changes
  useEffect(() => {
    console.log('[WorkflowEditor] workflow prop changed:', {
      workflowId: workflow?.id,
      stepsCount: workflow?.steps?.length
    });
  }, [workflow]);

  // Use custom hook for all state management
  const editor = useWorkflowEditor(workflow);

  // Backspace/Delete to remove selected step
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        const tag = e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
        if (!editor.selectedStepId) return;
        const step = editor.steps.find(s => s.id === editor.selectedStepId);
        if (step && !step.isStart && !step.isEnd) {
          e.preventDefault();
          editor.handleDeleteStep(deleteFile);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editor.selectedStepId, editor.steps, editor.handleDeleteStep]);

  const rawSelectedStep = editor.steps.find(s => s.id === editor.selectedStepId);
  const selectedStep = rawSelectedStep?.isLoopStart
    ? { ...rawSelectedStep, config: { ...rawSelectedStep.config, template: editor.claudeMdContent } }
    : rawSelectedStep;

  // Handle save to project
  const handleSaveToProject = async () => {
    editor.setIsSaving(true);
    try {
      // DEBUG: Log what we're working with
      console.group('[Bonzai] Write to Repo Debug');
      console.log('=== CURRENT STATE ===');
      console.log('editor.steps:', editor.steps);
      console.log('editor.steps count:', editor.steps.length);
      console.log('editor.steps details:', editor.steps.map(s => ({
        id: s.id,
        nodeId: s.nodeId,
        name: s.name,
        filepath: s.filepath,
        isStart: s.isStart,
        isEnd: s.isEnd,
        isLoopStart: s.isLoopStart
      })));

      console.log('=== COMPUTING CHANGESET ===');
      const changeset = computeChangeset(workflow?.steps || [], editor.steps);
      const hasFileChanges = changeset.filesToCreate.length > 0 || changeset.filesToUpdate.length > 0 || changeset.filesToRemove.length > 0 || changeset.filesToRename.length > 0;

      console.log('Original steps count:', (workflow?.steps || []).length);
      console.log('Current steps count:', editor.steps.length);
      console.log('Changeset:', JSON.stringify(changeset, null, 2));
      console.log('hasFileChanges:', hasFileChanges);
      console.log('claudeMdContent exists:', !!editor.claudeMdContent);
      console.log('claudeMdContent length:', editor.claudeMdContent?.length || 0);
      console.groupEnd();

      if (hasFileChanges) {
        const results = await applyChangeset(changeset);
        if (results.errors.length > 0) {
          results.errors.forEach(e => console.error(`  ${e.action} ${e.filepath}: ${e.error}`));
        }
      }

      if (editor.claudeMdContent) {
        try {
          console.log('[Bonzai] Writing CLAUDE.md...');
          console.log('[Bonzai] Content length:', editor.claudeMdContent.length);
          console.log('[Bonzai] Content:', editor.claudeMdContent.substring(0, 100));
          const result = await writeFile('CLAUDE.md', editor.claudeMdContent);
          console.log('[Bonzai] CLAUDE.md write result:', result);
        } catch (err) {
          console.error('[Bonzai] Failed to write CLAUDE.md:', err.message);
          console.error('[Bonzai] Full error:', err);
        }
      } else {
        console.log('[Bonzai] No claudeMdContent to write');
      }

      editor.savedSnapshotRef.current = JSON.stringify(editor.steps);
      editor.savedClaudeMdRef.current = editor.claudeMdContent;

      const savedWorkflow = saveWorkflow({
        name: workflow?.name || 'New Workflow',
        description: workflow?.description || 'Custom workflow configuration',
        steps: editor.steps,
        tags: workflow?.tags || ['custom'],
        created: editor.workflowCreated
      });

      recordPublishedWorkflow(editor.workflowTitle || workflow?.name || 'Select Workflow');

      if (onSave) {
        onSave(savedWorkflow);
      }

      // Close panels when writing to repo during onboarding
      if (showOnboarding) {
        editor.setIsNodePanelOpen(false);
        editor.setSelectedStepId(null);
      }
    } finally {
      console.log('[Bonzai] Setting isSaving to false');
      editor.setIsSaving(false);
    }
  };

  // Set the ref so the header instance can call this handler
  useEffect(() => {
    if (writeToRepoHandlerRef) {
      writeToRepoHandlerRef.current = handleSaveToProject;
    }
  }, [handleSaveToProject, writeToRepoHandlerRef]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: hideContentBelowHeader ? 'transparent' : '#fafafa',
      position: 'relative'
    }}>
      {!hideHeader && (
        <WorkflowEditorHeader
          showOnboarding={showOnboarding}
          canShowWriteButton={canShowWriteButton}
          hasUnsavedChanges={editor.hasUnsavedChanges}
          isSaving={editor.isSaving}
          currentWorkflowName={editor.currentWorkflowName}
          setCurrentWorkflowName={editor.setCurrentWorkflowName}
          isEditingWorkflowName={editor.isEditingWorkflowName}
          setIsEditingWorkflowName={editor.setIsEditingWorkflowName}
          savedWorkflowsList={editor.savedWorkflowsList}
          setSavedWorkflowsList={editor.setSavedWorkflowsList}
          steps={editor.steps}
          setSteps={editor.setSteps} setWorkflowTitle={editor.setWorkflowTitle}
          setSelectedStepId={editor.setSelectedStepId}
          savedSnapshotRef={editor.savedSnapshotRef}
          savedClaudeMdRef={editor.savedClaudeMdRef}
          workflowSwitchHintClicked={editor.workflowSwitchHintClicked}
          setWorkflowSwitchHintClicked={editor.setWorkflowSwitchHintClicked}
          onWriteToRepo={onWriteToRepo || handleSaveToProject}
          onOpenSignInModal={onOpenSignInModal}
        />
      )}


      {!hideContentBelowHeader && (
      <>{/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative', marginTop: 0 }}>
        {/* Editor — workflow canvas */}
        {showEditorToggle && (
          <>
            <div
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  editor.setSelectedStepId(null);
                  editor.setIsNodePanelOpen(false);
                }
              }}
              style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              overflowY: 'auto',
              padding: '200px 20px 100px',
              background: '#fafafa',
              backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
              backgroundSize: '24px 24px',
              position: 'relative'
            }}>
              <div style={{ width: '100%', maxWidth: '420px', minHeight: '200vh' }}>
                {/* Steps */}
                {(() => {
                  const startNode = editor.steps.find(s => s.isLoopStart);
                  const promptStep = editor.steps.find(s => s.isStart);
                  const respondStep = editor.steps.find(s => s.isEnd);

                  const startIdx = startNode ? editor.steps.indexOf(startNode) : -1;
                  const promptIdx = promptStep ? editor.steps.indexOf(promptStep) : -1;
                  const respondIdx = respondStep ? editor.steps.indexOf(respondStep) : -1;

                  const preLoopSteps = (startIdx >= 0 && promptIdx > startIdx + 1)
                    ? editor.steps.slice(startIdx + 1, promptIdx)
                    : [];
                  const loopMiddleSteps = (promptIdx >= 0 && respondIdx > promptIdx + 1)
                    ? editor.steps.slice(promptIdx + 1, respondIdx)
                    : [];

                  return (
                    <>
                      {!showOnboarding && (
                        <>
                          <div style={{
                            border: '1.5px dashed #c4b5fd',
                            borderRadius: '16px',
                            padding: (startNode && editor.claudeMdContent) ? '16px 12px' : '24px 12px',
                            margin: '0 -12px 0 -12px',
                            position: 'relative',
                            background: '#f5f3ff'
                          }}>
                            <span style={{
                              position: 'absolute',
                              top: '-9px',
                              left: '12px',
                              background: '#fafafa',
                              padding: '0 6px',
                              fontSize: '11px',
                              fontWeight: 600,
                              color: '#8b5cf6',
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em'
                            }}>
                              Before agent session
                            </span>
                            {startNode && editor.claudeMdContent ? (
                              <StepCard
                                step={startNode}
                                isSelected={editor.selectedStepId === startNode.id}
                                onClick={() => editor.handleSelectStep(startNode.id)}
                                onHover={(step) => editor.handleStepHover(step, onStepHover)}
                              />
                            ) : (
                              <button
                                onClick={async () => {
                                  try {
                                    const defaultContent = `# Project Instructions\n\nAdd your project-specific instructions here for Claude.\n`;
                                    await writeFile('CLAUDE.md', defaultContent);
                                    editor.setClaudeMdContent(defaultContent);
                                    // Refresh the page or trigger a re-fetch
                                    window.location.reload();
                                  } catch (err) {
                                    console.error('Failed to create CLAUDE.md:', err);
                                  }
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px',
                                  width: '100%',
                                  padding: '8px',
                                  background: 'transparent',
                                  border: '1px dashed #c4b5fd',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  color: '#8b5cf6',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#ede9fe'; e.currentTarget.style.borderColor = '#8b5cf6'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#c4b5fd'; }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                                  <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                                </svg>
                                Add CLAUDE.md
                              </button>
                            )}
                          </div>
                          <StepConnector />
                        </>
                      )}

                      {preLoopSteps.map((step) => (
                        <React.Fragment key={step.id}>
                          <StepCard
                            step={step}
                            isSelected={editor.selectedStepId === step.id}
                            onClick={() => editor.handleSelectStep(step.id)}
                            onHover={(step) => editor.handleStepHover(step, onStepHover)}
                          />
                          <StepConnector />
                        </React.Fragment>
                      ))}

                      <div style={{ position: 'relative' }}>
                        {promptStep && (
                          <>
                            <StepCard
                              step={promptStep}
                              isSelected={editor.selectedStepId === promptStep.id}
                              onClick={() => editor.handleSelectStep(promptStep.id)}
                              onHover={(step) => editor.handleStepHover(step, onStepHover)}
                            />
                            <StepConnector />
                          </>
                        )}

                        {/* Skills drop zone */}
                        <div
                          onDragOver={(e) => { e.preventDefault(); editor.setIsDragOver(true); }}
                          onDragLeave={() => editor.setIsDragOver(false)}
                          onDrop={editor.handleDropNode}
                          style={{
                            border: editor.isDragOver ? '2px dashed #8b5cf6' : '1.5px dashed #c4b5fd',
                            borderRadius: '16px',
                            padding: loopMiddleSteps.length > 0 ? '16px 12px' : '24px 12px',
                            margin: '0 -12px',
                            position: 'relative',
                            background: editor.isDragOver ? '#ede9fe' : '#f5f3ff',
                            transition: 'all 0.2s ease',
                            minHeight: loopMiddleSteps.length === 0 ? '60px' : undefined,
                            display: loopMiddleSteps.length === 0 ? 'flex' : 'block',
                            alignItems: loopMiddleSteps.length === 0 ? 'center' : undefined
                          }}
                        >
                          <span style={{
                            position: 'absolute',
                            top: '-9px',
                            left: '20px',
                            background: '#fafafa',
                            padding: '0 6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#8b5cf6',
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em'
                          }}>
                            During agent session
                          </span>
                          {loopMiddleSteps.length > 0 && (
                            loopMiddleSteps.map((step, i) => (
                              <div key={step.id} style={{ marginTop: i > 0 ? '8px' : 0 }}>
                                <StepCard
                                  step={step}
                                  isSelected={editor.selectedStepId === step.id}
                                  onClick={() => editor.handleSelectStep(step.id)}
                                  onHover={(step) => editor.handleStepHover(step, onStepHover)}
                                />
                              </div>
                            ))
                          )}
                          {/* Add skill button */}
                          <button
                            onClick={editor.handleOpenNodePanel}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              width: '100%',
                              padding: '8px',
                              marginTop: loopMiddleSteps.length > 0 ? '8px' : 0,
                              background: 'transparent',
                              border: '1px dashed #c4b5fd',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#8b5cf6',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#ede9fe'; e.currentTarget.style.borderColor = '#8b5cf6'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#c4b5fd'; }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                              <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                            </svg>
                            Add skill by clicking or dragging
                          </button>
                        </div>
                        <StepConnector />

                        {respondStep && (
                          <StepCard
                            step={respondStep}
                            isSelected={editor.selectedStepId === respondStep.id}
                            onClick={() => editor.handleSelectStep(respondStep.id)}
                            onHover={(step) => editor.handleStepHover(step, onStepHover)}
                          />
                        )}

                        {/* Loop connector removed */}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Floating action buttons */}
            {!editor.selectedStepId && !showOnboarding && (
              <>
                {/* Add node button */}
                <button
                  onClick={editor.handleOpenNodePanel}
                  style={{
                    position: 'absolute',
                    top: '72px',
                    right: '16px',
                    width: '44px',
                    height: '44px',
                    borderRadius: '8px',
                    border: editor.isNodePanelOpen ? '2px solid #3b82f6' : 'none',
                    background: '#f97316',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: editor.isNodePanelOpen ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : '0 2px 8px rgba(249, 115, 22, 0.3)',
                    transition: 'all 0.2s ease',
                    zIndex: 10
                  }}
                  title="Add node"
                  onMouseEnter={(e) => e.currentTarget.style.background = '#ea580c'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f97316'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#ffffff" style={{ width: '20px', height: '20px' }}>
                    <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                  </svg>
                </button>

                {/* File System Toggle button */}
                {!showOnboarding && (
                  <>
                    <button
                      onClick={() => {
                        if (onToggleFileSystem) {
                          onToggleFileSystem();
                        }
                        editor.setTerminalHintClicked(true);
                      }}
                      style={{
                        position: 'absolute',
                        top: '128px',
                        right: '16px',
                        width: '44px',
                        height: '44px',
                        borderRadius: '8px',
                        border: fileSystemOpen ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        background: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: fileSystemOpen ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        zIndex: 10
                      }}
                      title="Toggle file system"
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill={fileSystemOpen ? '#3b82f6' : '#374151'} style={{ width: '20px', height: '20px' }}>
                        <path d="M4.464 3.162A2 2 0 0 1 6.28 2h3.007a2 2 0 0 1 1.516.707l.924 1.087a.5.5 0 0 0 .379.177h3.615A1.5 1.5 0 0 1 17 5.47V6h-2V5.47a.47.47 0 0 0-.47-.47H11.21c-.552 0-1.07-.22-1.455-.618l-.924-1.087a.5.5 0 0 0-.38-.177H6.28a.5.5 0 0 0-.453.291l-.796 1.77H3.056L4.464 3.162Z" />
                        <path fillRule="evenodd" d="M2 7a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7Zm2 0h12v8H4V7Z" clipRule="evenodd" />
                      </svg>
                    </button>

                    {/* File system hint text */}
                    {!editor.terminalHintClicked && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '138px',
                          right: '76px',
                          fontSize: '13px',
                          color: '#9ca3af',
                          userSelect: 'none',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.2s ease',
                          zIndex: 10
                        }}
                      >
                        Workflows here write to your local filesystem →
                      </div>
                    )}
                  </>
                )}

                {/* Performance button */}
                <button
                  onClick={() => {
                    editor.setShowExperiment(prev => !prev);
                    editor.setPerformanceHintClicked(true);
                    if (!editor.showExperiment) {
                      editor.setIsNodePanelOpen(false);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '184px',
                    right: '16px',
                    width: '44px',
                    height: '44px',
                    borderRadius: '8px',
                    border: editor.showExperiment ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                    background: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: editor.showExperiment ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease',
                    zIndex: 10
                  }}
                  title="Performance"
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill={editor.showExperiment ? '#3b82f6' : '#374151'} style={{ width: '20px', height: '20px' }}>
                    <path fillRule="evenodd" d="M12.577 4.878a.75.75 0 0 1 .919-.53l4.78 1.281a.75.75 0 0 1 .531.919l-1.281 4.78a.75.75 0 0 1-1.449-.387l.81-3.022a19.407 19.407 0 0 0-5.594 5.203.75.75 0 0 1-1.139.093L7 10.06l-4.72 4.72a.75.75 0 0 1-1.06-1.06l5.25-5.25a.75.75 0 0 1 1.06 0l3.048 3.047a20.902 20.902 0 0 1 5.532-5.42l-2.922.782a.75.75 0 0 1-.53-.919Z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Performance hint text */}
                {!editor.performanceHintClicked && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '196px',
                      right: '76px',
                      fontSize: '13px',
                      color: '#9ca3af',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease',
                      zIndex: 10
                    }}
                  >
                   Measure which workflows work best →
                  </div>
                )}
              </>
            )}

            </div>

            {/* Config Panel */}
            {selectedStep && !editor.isNodePanelOpen && (
              <ConfigPanel
                step={selectedStep}
                onConfigChange={editor.handleConfigChange}
                onDelete={() => editor.handleDeleteStep(deleteFile)}
                canDelete={selectedStep && !selectedStep.isStart && !selectedStep.isEnd && !selectedStep.isLoopStart}
                onClose={editor.handleClosePanel}
                onOpenNodePanel={editor.handleOpenNodePanel}
              />
            )}

            {/* Node Panel */}
            <NodePanel
              isOpen={editor.isNodePanelOpen}
              onClose={editor.handleCloseNodePanel}
              initialSection={null}
              onSelectNode={(node) => {
                console.group('[ADD SKILL] onSelectNode called');
                console.log('Node being added:', {
                  id: node.id,
                  name: node.name,
                  description: node.description,
                  filepath: node.filepath,
                  hasTemplate: !!node.template
                });

                const newStepId = `step-${Date.now()}`;
                editor.setSteps(prev => {
                  console.log('Current steps before adding:', prev.length);
                  console.log('Steps:', prev.map(s => ({ id: s.id, nodeId: s.nodeId, name: s.name, filepath: s.filepath })));

                  // Figure out where this node lands so we can set parentNode
                  let targetIndex;
                  if (editor.insertAtIndex !== null) {
                    targetIndex = editor.insertAtIndex;
                  } else {
                    const endIdx = prev.findIndex(s => s.isEnd);
                    targetIndex = endIdx === -1 ? prev.length : endIdx;
                  }
                  const parentStep = targetIndex > 0 ? prev[targetIndex - 1] : null;

                  const defaultConfig = getDefaultConfig(node.id, node);
                  const skillName = defaultConfig.skillName || node.id;

                  const newStep = {
                    id: newStepId,
                    nodeId: node.id,
                    name: node.name,
                    subtitle: node.description,
                    icon: node.icon,
                    iconBg: `${node.color}15`,
                    parentNode: parentStep ? parentStep.id : null,
                    filepath: node.filepath || `.claude/skills/${skillName}/SKILL.md`,
                    isPublished: node.isPublished || false,
                    rating: node.rating || 0,
                    config: defaultConfig,
                    fileIds: []
                  };

                  console.log('New step created:', {
                    id: newStep.id,
                    nodeId: newStep.nodeId,
                    name: newStep.name,
                    filepath: newStep.filepath,
                    skillName: skillName,
                    hasTemplate: !!newStep.config?.template
                  });

                  const newSteps = [...prev];
                  newSteps.splice(targetIndex, 0, newStep);

                  // Update the step after the inserted one to point to the new step as its parent
                  if (targetIndex + 1 < newSteps.length) {
                    newSteps[targetIndex + 1] = { ...newSteps[targetIndex + 1], parentNode: newStep.id };
                  }

                  console.log('Steps after adding:', newSteps.length);
                  console.log('All steps:', newSteps.map(s => ({ id: s.id, nodeId: s.nodeId, name: s.name, filepath: s.filepath })));
                  console.groupEnd();

                  return newSteps;
                });
                editor.setInsertAtIndex(null);
                if (onSkillAdded) onSkillAdded();
              }}
            />

            {/* Performance Chart Panel */}
            {editor.showExperiment && (
              <ExperimentChart onClose={() => editor.setShowExperiment(false)} />
            )}
          </>
        )}

        {/* Chats Tab */}
        {editor.activeTab === 'chats' && activeLoops.chat && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' }}>
            <div style={{ flex: 1, overflowY: 'auto', paddingTop: '20px' }}>
              {editor.chatMessages.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#6b7280'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <span style={{ fontSize: '32px' }}>🤖</span>
                  </div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                    What's on the agenda today?
                  </h3>
                  <p style={{ margin: '8px 0 0', fontSize: '14px' }}>
                    Start a conversation with {workflow.name}
                  </p>
                </div>
              ) : (
                <>
                  {editor.chatMessages.map(conv => {
                    const { userMessage, agentResponse } = conv;
                    const fileChanges = agentResponse?.fileChanges || [];
                    const isHovered = editor.hoveredConversation === conv.id;

                    return (
                      <div
                        key={conv.id}
                        style={{
                          marginBottom: '24px',
                          borderLeft: '2px solid #e5e7eb',
                          marginLeft: '20px',
                          position: 'relative'
                        }}
                        onMouseEnter={() => editor.setHoveredConversation(conv.id)}
                        onMouseLeave={() => editor.setHoveredConversation(null)}
                      >
                        <div style={{
                          position: 'absolute',
                          left: '-5px',
                          top: '16px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: isHovered ? '#3b82f6' : '#d1d5db',
                          transition: 'background 0.15s'
                        }} />

                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          padding: '0 20px 12px 24px'
                        }}>
                          <div style={{
                            maxWidth: '70%',
                            padding: '12px 16px',
                            borderRadius: '16px 16px 4px 16px',
                            background: '#ef4444',
                            color: '#fff',
                            fontSize: '14px',
                            lineHeight: 1.5
                          }}>
                            {userMessage.content}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          padding: '0 20px 12px 24px',
                          marginTop: '-8px'
                        }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>You {userMessage.time}</span>
                        </div>

                        {agentResponse && (
                          <div style={{ padding: '0 20px 0 24px', position: 'relative' }}>
                            <div style={{
                              padding: '12px 16px',
                              borderRadius: '16px',
                              background: '#f3f4f6',
                              color: '#111827',
                              fontSize: '14px',
                              lineHeight: 1.5,
                              whiteSpace: 'pre-wrap'
                            }}>
                              {agentResponse.content}
                            </div>

                            {fileChanges.length > 0 && fileChanges.some(fc => fc.diffs && fc.diffs.length > 0) && (
                              <div style={{ marginTop: '12px' }}>
                                {fileChanges.filter(fc => fc.diffs && fc.diffs.length > 0).map((fc, idx) => (
                                  <CodeDiff
                                    key={idx}
                                    diffs={fc.diffs}
                                    fileName={fc.file}
                                    additions={fc.additions}
                                    deletions={fc.deletions}
                                    onHover={editor.setHoveredFile}
                                    isHighlighted={editor.hoveredFile === fc.file}
                                  />
                                ))}
                              </div>
                            )}

                            {fileChanges.length > 0 && (
                              <FileChangesIndicator
                                fileChanges={fileChanges} hoveredFile={editor.hoveredFile}
                                onFileHover={editor.setHoveredFile}
                                expanded={editor.expandedFiles[conv.id] !== false}
                                onToggleExpanded={() => editor.setExpandedFiles(prev => ({ ...prev, [conv.id]: !prev[conv.id] }))}
                              />
                            )}

                            {agentResponse.logs && agentResponse.logs.length > 0 && (
                              <div style={{ marginTop: '12px' }}>
                                <button
                                  onClick={() => editor.setExpandedLogs(prev => ({ ...prev, [conv.id]: !prev[conv.id] }))}
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                    color: '#6b7280',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  {editor.expandedLogs[conv.id] ? 'Hide' : 'Show'} logs ({agentResponse.logs.length})
                                </button>

                                {editor.expandedLogs[conv.id] && (
                                  <div style={{
                                    marginTop: '8px',
                                    padding: '12px',
                                    background: '#1f2937',
                                    borderRadius: '8px',
                                    fontFamily: 'monospace',
                                    fontSize: '12px'
                                  }}>
                                    {agentResponse.logs.map((log, idx) => (
                                      <div key={idx} style={{ marginBottom: idx < agentResponse.logs.length - 1 ? '4px' : 0 }}>
                                        <span style={{ color: '#6b7280' }}>[{log.time}]</span>{' '}
                                        <span style={{ color: '#d1d5db' }}>{log.text}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginTop: '12px',
                              fontSize: '11px',
                              color: '#9ca3af'
                            }}>
                              <span>Agent</span>
                              <span>{agentResponse.time}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={editor.messagesEndRef} />
                </>
              )}
            </div>

            {/* Chat Input */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              background: '#fafafa'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '12px',
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '16px',
                padding: '8px 8px 8px 16px'
              }}>
                <button style={{
                  padding: '8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280'
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                    <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                  </svg>
                </button>
                <textarea
                  value={editor.chatInput}
                  onChange={(e) => editor.setChatInput(e.target.value)}
                  placeholder="Ask anything..."
                  rows={1}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                    fontSize: '14px',
                    lineHeight: '20px',
                    padding: '8px 0',
                    background: 'transparent',
                    fontFamily: 'inherit',
                    maxHeight: '120px'
                  }}
                />
                <button
                  disabled={!editor.chatInput.trim()}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: editor.chatInput.trim() ? '#ef4444' : '#f3f4f6',
                    border: 'none',
                    cursor: editor.chatInput.trim() ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.15s'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill={editor.chatInput.trim() ? '#fff' : '#9ca3af'} style={{ width: '18px', height: '18px' }}>
                    <path d="M3.105 2.288a.75.75 0 0 0-.826.95l1.414 4.926A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.897 28.897 0 0 0 15.293-7.155.75.75 0 0 0 0-1.114A28.897 28.897 0 0 0 3.105 2.288Z" />
                  </svg>
                </button>
              </div>
              <div style={{
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                color: '#9ca3af'
              }}>
                <span>Using </span>
                <span style={{ fontWeight: 500, color: '#6b7280', margin: '0 4px' }}>{workflow.name}</span>
              </div>
            </div>
          </div>
        )}

        {/* Evaluations Tab */}
        {editor.activeTab === 'evaluations' && activeLoops.evaluators && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', overflowY: 'auto' }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: '24px'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '12px',
                background: workflow.evaluation?.currentScore < 0.05 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ fontSize: '28px' }}>🤖</span>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>{workflow.name}</h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
                  {workflow.evaluation?.totalLifetimePushes || 0} lifetime pushes • {workflow.evaluation?.versions?.length || 0} versions
                </p>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{
                  fontSize: '28px',
                  fontWeight: 600,
                  color: (workflow.evaluation?.currentScore || 0) < 0.03 ? '#16a34a' :
                         (workflow.evaluation?.currentScore || 0) < 0.06 ? '#ca8a04' : '#dc2626'
                }}>
                  {(workflow.evaluation?.currentScore || 0).toFixed(3)}
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>reverts per token</div>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              <div style={{
                padding: '12px 20px',
                background: '#fafafa',
                borderBottom: '1px solid #e5e7eb',
                fontSize: '11px',
                fontWeight: 600,
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Versions
              </div>
              {(workflow.evaluation?.versions || []).map(version => (
                <div
                  key={version.id}
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #f3f4f6',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '32px',
                    borderRadius: '6px',
                    background: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '14px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#374151'
                  }}>
                    {version.number}
                  </div>
                  <span style={{
                    fontSize: '10px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: version.status === 'active' ? '#dbeafe' : '#f3f4f6',
                    color: version.status === 'active' ? '#2563eb' : '#6b7280',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                    marginRight: '16px'
                  }}>
                    {version.status}
                  </span>
                  <div style={{ minWidth: '70px', marginRight: '20px' }}>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: version.score < 0.03 ? '#16a34a' : version.score < 0.06 ? '#ca8a04' : '#dc2626'
                    }}>
                      {version.score.toFixed(3)}
                    </span>
                    <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '4px' }}>r/t</span>
                  </div>
                  <div style={{ display: 'flex', gap: '20px', marginLeft: 'auto' }}>
                    <div style={{ textAlign: 'center', minWidth: '45px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>{version.pushes}</div>
                      <div style={{ fontSize: '10px', color: '#9ca3af' }}>pushes</div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: '45px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#16a34a' }}>{version.cleanPushes}</div>
                      <div style={{ fontSize: '10px', color: '#9ca3af' }}>clean</div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: '45px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#dc2626' }}>{version.revertedPushes}</div>
                      <div style={{ fontSize: '10px', color: '#9ca3af' }}>reverted</div>
                    </div>
                  </div>
                </div>
              ))}
              {(!workflow.evaluation?.versions || workflow.evaluation.versions.length === 0) && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ fontSize: '14px' }}>No versions yet</p>
                  <p style={{ fontSize: '13px', marginTop: '4px' }}>Run the workflow to generate evaluation data</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <SaveWorkflowModal showSaveModal={editor.showSaveModal} setShowSaveModal={editor.setShowSaveModal} editingWorkflowId={editor.editingWorkflowId} setEditingWorkflowId={editor.setEditingWorkflowId} saveName={editor.saveName} setSaveName={editor.setSaveName} saveDate={editor.saveDate} setSaveDate={editor.setSaveDate} setWorkflowTitle={editor.setWorkflowTitle}
        steps={editor.steps}
      />
      </>
      )}

    </div>
  );
}

export default WorkflowEditor;
export { WorkflowEditor };
